/**
 * Действия режимов «Цифры», «Пунктуация» и «Специальные символы».
 *
 * Занятие целиком — один прогон набора: цикл Memory Loop здесь не нужен,
 * пользователь ещё не знает клавиши, а не забыл её.
 */

import { useCallback } from 'react';
import type { TypingSession } from '@type2x/typing-engine';
import type { Workspace } from '@type2x/feature-personal-workspace';
import type { SymbolMode } from '@type2x/feature-symbols';
import type { SessionPayload } from '@type2x/feature-sync';

import type { Runtime } from '../runtime.js';
import type { AppScreen } from '../state/app-state.js';
import { finishSymbols, symbolsScreen } from '../state/symbols.js';

/** Действия режимов, которые приложение передаёт экранам. */
export interface SymbolsActions {
  readonly onStartSymbols: (mode: SymbolMode) => void;
  readonly onSymbolsDone: (run: TypingSession) => void;
}

/** Что нужно режимам от приложения. */
export interface SymbolsDeps {
  readonly workspace: Workspace | null;
  readonly commit: (next: Workspace) => Promise<void>;
  readonly screen: AppScreen;
  readonly setScreen: (screen: AppScreen) => void;
  readonly submit: (payload: SessionPayload) => Promise<unknown>;
}

export function useSymbols(runtime: Runtime, deps: SymbolsDeps): SymbolsActions {
  const { workspace, commit, screen, setScreen, submit } = deps;

  const onStartSymbols = useCallback(
    (mode: SymbolMode) => {
      if (workspace === null) return;
      setScreen(symbolsScreen(runtime, workspace, mode));
    },
    [runtime, workspace, setScreen],
  );

  const onSymbolsDone = useCallback(
    (run: TypingSession) => {
      if (workspace === null || screen.name !== 'symbols') return;

      const transition = finishSymbols(runtime, workspace, screen.plan, run, screen.sessionId);
      void commit(transition.workspace);
      setScreen(transition.screen);

      // Отправка идёт после показа результата: сеть не задерживает экран.
      if (transition.sessionPayload !== undefined) {
        void submit(transition.sessionPayload);
      }
    },
    [runtime, workspace, screen, commit, setScreen, submit],
  );

  return { onStartSymbols, onSymbolsDone };
}
